import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * AnimatedCounter
 * @param to The final number to count up to.
 * @param duration The total duration (in seconds) of the counting animation.
 */
interface AnimatedCounterProps {
  to: number;
  duration?: number; // default 2 seconds
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({
  to,
  duration = 2,
}) => {
  // currentValue: holds the integer count at any moment
  const [currentValue, setCurrentValue] = useState(0);

  useEffect(() => {
    let start: number | null = null;
    const totalFrames = Math.round(60 * duration); // 60 FPS * duration
    const increment = (to - 0) / totalFrames; // from 0 to `to`
    let frame = 0;

    function animateCount(timestamp: number) {
      if (!start) start = timestamp;
      frame++;
      // newVal is how far we've incremented so far
      const newVal = 0 + increment * frame;
      // floor it to show integer steps
      setCurrentValue(Math.floor(newVal));

      if (frame < totalFrames) {
        requestAnimationFrame(animateCount);
      } else {
        // Ensure we end exactly at `to`
        setCurrentValue(to);
      }
    }

    // Start the animation
    requestAnimationFrame(animateCount);
  }, [to, duration]);

  // Convert the current value to a string of digits
  const currentStr = currentValue.toString();

  return (
    <div className="inline-flex min-w-3 h-auto gap-[1px]">
      {currentStr.split("").map((digit, index) => (
        <Digit key={index} digit={digit} />
      ))}
    </div>
  );
};

/**
 * Digit component
 * Renders a single digit with a vertical slide animation whenever `digit` changes.
 */
interface DigitProps {
  digit: string;
}

const Digit: React.FC<DigitProps> = ({ digit }) => {
  return (
    <div className="relative w-[1ch] h-full min-h-fit overflow-hidden inline-block text-center align-middle">
      <AnimatePresence initial={false}>
        <motion.div
          key={digit}
          initial={{ y: "100%" }}   // start below
          animate={{ y: "0%" }}     // slide up into position
          exit={{ y: "-100%" }}     // slide out above
          transition={{ duration: 0.3 }}
          className="absolute w-full"
        >
          {digit}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default AnimatedCounter;
